import React, { useState } from "react";
import "./newsArticle.scss";
import { newsArticlProps } from "./newsArticle.types";
import NewsClasses from "../NewsClasses/NewsClasses";
import NerAanalysis from "../NerAanalysis/NerAanalysis";
import LaunchIcon from "@mui/icons-material/Launch";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

/**
 * NewsArticle Component.
 * Displays summary, organizations, news type, events and full article modal.
 * Called in \src\Views\News\News.tsx
 */
const NewsArticle = ({ singleNews }: newsArticlProps) => {
  const [isFullArticleOpen, setIsFullArticleOpen] = useState(false);

  let newsURL: string | null = null;
  if (singleNews["ID"]) {
    const idParts = singleNews["ID"].split("+UNIQUE_T_AND_N_ID_SEPARATOR+");
    newsURL = idParts.length > 1 ? idParts[1]?.trim() : null;
  }

  const sentiment: string = singleNews.News_Sentiment || "";
  const sentimentScore: number | null =
    singleNews.News_Sentiment_Score != null
      ? Math.round(singleNews.News_Sentiment_Score * 100)
      : null;

  const isPositive = sentiment.toLowerCase() === "positive";
  const isNegative = sentiment.toLowerCase() === "negative";
  const sentimentClass = isPositive
    ? "na-sentiment--positive"
    : isNegative
      ? "na-sentiment--negative"
      : "na-sentiment--neutral";

  const source: string = singleNews.News_Source || "";
  const dateRaw: string = singleNews.News_Time_Stamp || singleNews.News_date || "";
  const dateFormatted = dateRaw
    ? new Date(dateRaw).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

  return (
    <div className="na-detail-panel">
      <div className="na-detail-inner">

        {/* ── Header strip ── */}
        <div className={`na-header-strip ${sentimentClass}`}>
          <div className="na-header-meta">
            {source && <span className="na-source">{source}</span>}
            {source && dateFormatted && !dateFormatted.includes("Invalid") && <span className="na-meta-dot">·</span>}
            {dateFormatted && !dateFormatted.includes("Invalid") && <span className="na-date">{dateFormatted}</span>}
          </div>
          <div className="na-header-right">
            {newsURL && (
              <a
                href={newsURL}
                target="_blank"
                rel="noreferrer noopener"
                className="na-detail-link-btn"
                title="Open original article"
              >
                <LaunchIcon style={{ fontSize: 15 }} />
              </a>
            )}
          </div>
        </div>

        {/* ── Title ── */}
        {singleNews.News_Title && (
          <div className="na-title-block">
            <h2 className="na-title">{singleNews.News_Title}</h2>
          </div>
        )}

        {/* ── Summary ── */}
        <div className="na-summary-block">
          <p className="na-summary-text">
            {singleNews.News_Summary || "No summary available."}
          </p>
        </div>

        {/* ── Organizations + News Type row ── */}
        {(singleNews.News_ner_org_freq || singleNews.News_Classes) && (
          <div className="na-tags-row">
            {singleNews.News_ner_org_freq && (
              <div className="na-tags-group">
                <span className="na-tags-label">Companies</span>
                <NerAanalysis data={singleNews.News_ner_org_freq} />
              </div>
            )}
            {singleNews.News_Classes && (
              <div className="na-tags-group">
                <span className="na-tags-label">Category</span>
                <NewsClasses data={singleNews.News_Classes} />
              </div>
            )}
          </div>
        )}

        {/* ── Events Detected ── */}
        {singleNews.Events_Detected && singleNews.Events_Detected.length > 0 && (
          <div className="na-events-block">
            <span className="na-tags-label">Events Detected</span>
            <div className="na-events-list">
              {(singleNews.Events_Detected as string[]).map((event, index) => (
                <div key={index} className="na-event-row">
                  <span className="na-event-index">{index + 1}</span>
                  <p className="na-event-text">{event}</p>
                  {singleNews.Labels?.[index] && (
                    <span className="na-event-label">{singleNews.Labels[index]}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Footer ── */}
        <div className="na-detail-footer">
          <button
            className="na-detail-read-btn"
            onClick={() => setIsFullArticleOpen(true)}
            type="button"
          >
            Read Full Article
          </button>
        </div>

      </div>

      {/* ── Modal ── */}
      <Dialog
        open={isFullArticleOpen}
        onClose={() => setIsFullArticleOpen(false)}
        maxWidth="md"
        fullWidth
        className="full-article-modal"
      >
        <DialogContent className="modal-content">
          <div className="modal-header">
            <h6>Full Article</h6>
            <IconButton
              aria-label="close"
              onClick={() => setIsFullArticleOpen(false)}
              className="close-button"
            >
              <CloseIcon />
            </IconButton>
          </div>

          {singleNews.Actual_News && (
            <div className="modal-article-content">
              <div
                className="article-text"
                dangerouslySetInnerHTML={{ __html: singleNews.Actual_News }}
              />
            </div>
          )}

          {newsURL && (
            <div className="modal-article-link">
              <p><strong>Source:</strong></p>
              <a target="_blank" rel="noreferrer noopener" href={newsURL}>
                {singleNews["News_Title"] || "Read Original Article"}
              </a>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NewsArticle;
